/**
 * UI helpers for Leave Group band configuration.
 *
 * Business terminology (display):
 *   Band 1 — Highest Performance (most generous CL allocation)
 *   Band 2 — Medium Performance
 *   Band 3 — Lowest Performance (least generous CL allocation)
 *
 * Matching rules (example thresholds):
 *   Band 1: averageOfficeHours >= 6          (lower-limit only)
 *   Band 2: 4.5 <= averageOfficeHours < 6    (both limits)
 *   Band 3: averageOfficeHours < 4.5         (upper-limit only)
 *           — includes 0 hours and non-calculable / missing prior-quarter hours
 *
 * Editor state keys map technical threshold-role shapes to business bands:
 *   highest (lower-only) → Band 1 — Highest Performance
 *   middle (both limits) → Band 2 — Medium Performance
 *   lowest (upper-only)  → Band 3 — Lowest Performance
 * List order in the serialized bands array is irrelevant; limits define the role.
 *
 * Each band's Q1–Q4 Casual Leave allocations are the entitlement source of truth.
 */

const emptyQuarterAllocations = () => ({
  q1Allocation: '',
  q2Allocation: '',
  q3Allocation: '',
  q4Allocation: '',
});

export const emptyBandEditorState = () => ({
  lowest: { upperLimit: '', ...emptyQuarterAllocations() },
  middle: { lowerLimit: '', upperLimit: '', ...emptyQuarterAllocations() },
  highest: { lowerLimit: '', ...emptyQuarterAllocations() },
});

const allocationFields = [
  'q1Allocation',
  'q2Allocation',
  'q3Allocation',
  'q4Allocation',
];

const stringAllocationsFromBand = (band) => {
  const result = {};
  allocationFields.forEach((field) => {
    result[field] = band?.[field] != null ? String(band[field]) : '';
  });
  return result;
};

export const bandsToEditorState = (bandConfiguration) => {
  const bands = bandConfiguration?.bands;
  if (!Array.isArray(bands) || bands.length !== 3) {
    return emptyBandEditorState();
  }

  const state = emptyBandEditorState();
  bands.forEach((band) => {
    if (!band) return;
    const hasLower = band.lowerLimit != null && band.lowerLimit !== '';
    const hasUpper = band.upperLimit != null && band.upperLimit !== '';
    if (!hasLower && hasUpper) {
      state.lowest = {
        upperLimit: String(band.upperLimit),
        ...stringAllocationsFromBand(band),
      };
    } else if (hasLower && hasUpper) {
      state.middle = {
        lowerLimit: String(band.lowerLimit),
        upperLimit: String(band.upperLimit),
        ...stringAllocationsFromBand(band),
      };
    } else if (hasLower && !hasUpper) {
      state.highest = {
        lowerLimit: String(band.lowerLimit),
        ...stringAllocationsFromBand(band),
      };
    }
  });
  return state;
};

const parseOptionalNumber = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const parseRequiredInt = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return NaN;
  }
  return parsed;
};

const parseBandAllocations = (bandState) => ({
  q1Allocation: parseRequiredInt(bandState.q1Allocation),
  q2Allocation: parseRequiredInt(bandState.q2Allocation),
  q3Allocation: parseRequiredInt(bandState.q3Allocation),
  q4Allocation: parseRequiredInt(bandState.q4Allocation),
});

/**
 * Derived annual total for display only (Q1+Q2+Q3+Q4). Not editable / not submitted.
 */
export const deriveAnnualTotal = (bandState) => {
  const allocations = parseBandAllocations(bandState || {});
  const values = Object.values(allocations);
  if (values.some((v) => v === null || Number.isNaN(v))) {
    return null;
  }
  return values.reduce((sum, v) => sum + v, 0);
};

/**
 * Serialize fixed UI rows into generic BandDTO[].
 */
export const editorStateToBandConfiguration = (editorState) => {
  const lowestUpper = parseOptionalNumber(editorState.lowest.upperLimit);
  const middleLower = parseOptionalNumber(editorState.middle.lowerLimit);
  const middleUpper = parseOptionalNumber(editorState.middle.upperLimit);
  const highestLower = parseOptionalNumber(editorState.highest.lowerLimit);

  return {
    bands: [
      {
        lowerLimit: null,
        upperLimit: lowestUpper,
        ...parseBandAllocations(editorState.lowest),
      },
      {
        lowerLimit: middleLower,
        upperLimit: middleUpper,
        ...parseBandAllocations(editorState.middle),
      },
      {
        lowerLimit: highestLower,
        upperLimit: null,
        ...parseBandAllocations(editorState.highest),
      },
    ],
  };
};

const validateAllocationFields = (bandState, roleErrors) => {
  let hasError = false;
  allocationFields.forEach((field) => {
    const value = parseRequiredInt(bandState[field]);
    if (value === null || Number.isNaN(value) || value < 0) {
      roleErrors[field] = 'Enter a non-negative whole number';
      hasError = true;
    }
  });
  return hasError;
};

export const validateBandEditorState = (editorState) => {
  const errors = {
    lowest: {},
    middle: {},
    highest: {},
    general: null,
  };

  const lowestUpper = parseOptionalNumber(editorState.lowest.upperLimit);
  const middleLower = parseOptionalNumber(editorState.middle.lowerLimit);
  const middleUpper = parseOptionalNumber(editorState.middle.upperLimit);
  const highestLower = parseOptionalNumber(editorState.highest.lowerLimit);

  let hasError = false;

  if (lowestUpper === null || Number.isNaN(lowestUpper)) {
    errors.lowest.upperLimit = 'Upper limit is required';
    hasError = true;
  }
  if (validateAllocationFields(editorState.lowest, errors.lowest)) {
    hasError = true;
  }

  if (middleLower === null || Number.isNaN(middleLower)) {
    errors.middle.lowerLimit = 'Lower limit is required';
    hasError = true;
  }
  if (middleUpper === null || Number.isNaN(middleUpper)) {
    errors.middle.upperLimit = 'Upper limit is required';
    hasError = true;
  }
  if (
    middleLower != null &&
    !Number.isNaN(middleLower) &&
    middleUpper != null &&
    !Number.isNaN(middleUpper) &&
    middleLower >= middleUpper
  ) {
    errors.middle.upperLimit = 'Upper limit must be greater than lower limit';
    hasError = true;
  }
  if (validateAllocationFields(editorState.middle, errors.middle)) {
    hasError = true;
  }

  if (highestLower === null || Number.isNaN(highestLower)) {
    errors.highest.lowerLimit = 'Lower limit is required';
    hasError = true;
  }
  if (validateAllocationFields(editorState.highest, errors.highest)) {
    hasError = true;
  }

  if (
    !hasError &&
    lowestUpper != null &&
    middleLower != null &&
    lowestUpper !== middleLower
  ) {
    errors.general =
      'Band 3 upper limit must equal Band 2 lower limit (continuous range)';
    hasError = true;
  }
  if (
    !hasError &&
    middleUpper != null &&
    highestLower != null &&
    middleUpper !== highestLower
  ) {
    errors.general =
      'Band 2 upper limit must equal Band 1 lower limit (continuous range)';
    hasError = true;
  }

  return { isValid: !hasError, errors };
};

const formatBandQuarterLine = (label, band) => {
  if (!band) return null;
  const q1 = band.q1Allocation;
  const q2 = band.q2Allocation;
  const q3 = band.q3Allocation;
  const q4 = band.q4Allocation;
  if ([q1, q2, q3, q4].some((v) => v == null)) {
    return `${label}: (allocations incomplete)`;
  }
  return `${label}: Q1 ${q1} / Q2 ${q2} / Q3 ${q3} / Q4 ${q4}`;
};

export const formatBandSummary = (bandConfiguration) => {
  const bands = bandConfiguration?.bands;
  if (!Array.isArray(bands) || bands.length === 0) {
    return '—';
  }

  const classified = { lowest: null, middle: null, highest: null };
  bands.forEach((band) => {
    if (!band) return;
    const hasLower = band.lowerLimit != null;
    const hasUpper = band.upperLimit != null;
    if (!hasLower && hasUpper) classified.lowest = band;
    else if (hasLower && hasUpper) classified.middle = band;
    else if (hasLower && !hasUpper) classified.highest = band;
  });

  const parts = [
    formatBandQuarterLine('Band 1 (Highest Performance)', classified.highest),
    formatBandQuarterLine('Band 2 (Medium Performance)', classified.middle),
    formatBandQuarterLine('Band 3 (Lowest Performance)', classified.lowest),
  ].filter(Boolean);

  const annualParts = [];
  [classified.highest, classified.middle, classified.lowest].forEach((band) => {
    if (!band) return;
    const values = [
      band.q1Allocation,
      band.q2Allocation,
      band.q3Allocation,
      band.q4Allocation,
    ];
    if (values.every((v) => v != null && Number.isFinite(Number(v)))) {
      annualParts.push(
        values.reduce((sum, v) => sum + Number(v), 0),
      );
    }
  });
  if (annualParts.length === 3) {
    parts.push(`Annual (by band): ${annualParts.join(' / ')}`);
  }

  return parts.length ? parts.join(' · ') : '—';
};
